import type {
  MsgParentToChild,
  MsgChildToParent,
  PayloadError,
  BridgeMeta,
} from "../shared/types"
import { createId } from "../shared/ids"
import { isAllowedOrigin, isEnvelope, isEventType } from "../shared/validate"
import { BRIDGE_EVENTS, BRIDGE_PROTOCOL } from "../shared/constants"

type ChildBridgeOptions = {
  parentTargetOrigin: string // exact origin (no "*")
  allowedParentOrigins: string[]
  metaGetter?: () => BridgeMeta // iframe.contentWindow
  onError?: (error: PayloadError) => void
  onReady?: () => void
  onEvent?: (msg: MsgParentToChild, meta: BridgeMeta) => Promise<void> | void
}

export const createChildBridge = (options: ChildBridgeOptions) => {
  const bridgeState = {
    ready: false,
  }

  const isReady = () => bridgeState.ready

  const postToParent = (msg: MsgChildToParent) => {
    window.parent.postMessage(msg, options.parentTargetOrigin)
  }

  const sendSync = () => {
    postToParent({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.SYNC,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload: {},
    })
  }

  const sendAck = () => {
    postToParent({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.ACK,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload: { status: "ok" },
    })
    bridgeState.ready = true
    options.onReady?.()
  }

  const disconnect = () => {
    postToParent({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.DISCONNECT,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload: {},
    })
    bridgeState.ready = false
  }

  const sendError = (type: PayloadError["type"] | string, message: string) => {
    postToParent({
      protocol: BRIDGE_PROTOCOL.CHANNEL,
      version: BRIDGE_PROTOCOL.VERSION,
      type: BRIDGE_EVENTS.ERROR,
      id: createId(),
      ts: Date.now(),
      meta: options?.metaGetter?.() ?? {},
      payload: { type, message },
    })
  }

  const onMessage = async (event: MessageEvent) => {
    if (!isAllowedOrigin(event.origin, options.allowedParentOrigins)) return
    if (!isEnvelope(event.data)) return
    if (!isEventType(event.data.type)) return

    const msg = event.data as unknown as MsgParentToChild

    if (msg.type === BRIDGE_EVENTS.SYNC_ACK) {
      sendAck()
      return
    }

    if (msg.type === BRIDGE_EVENTS.ERROR) {
      options.onError?.(msg.payload.message)
      return
    }

    try {
      const meta = msg?.meta || {}
      await options.onEvent(msg, { origin: event.origin, ...meta })
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown error"
      sendError(msg.type, message)
      options?.onError?.({
        type: msg.type,
        message,
      })
    }
  }

  const attach = () => window.addEventListener("message", onMessage)
  const detach = () => window.removeEventListener("message", onMessage)

  // public actions (only after ready)
  const ensureReady = () => {
    if (!isReady()) throw new Error("bridge not ready (sync not completed)")
  }

  const nav = {
    openSearch: (q: string) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_SEARCH,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { q },
      })
    },
    openPlace: (id: string) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_PLACE,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { id },
      })
    },
    showDirections: (from: string, to: string) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_SHOW_DIRECTIONS,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { from, to },
      })
    },
    startNavigation: (from: string, to: string) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_START_NAVIGATION,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { from, to },
      })
    },
    nextStepNavigation: (from: string, to: string, step: number) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_NEXT_STEP_NAVIGATION,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { from, to, step },
      })
    },
    prevStepNavigation: (from: string, to: string, step: number) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_PREV_STEP_NAVIGATION,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { from, to, step },
      })
    },
    openServices: () => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_SERVICES,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: {},
      })
    },
    openEvents: () => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_EVENTS,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: {},
      })
    },
    openEventDetail: (id: string) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_EVENT_DETAIL,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { id },
      })
    },
    openPromotions: () => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_PROMOTIONS,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: {},
      })
    },
    openPromotionDetail: (id: string) => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_PROMOTION_DETAIL,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: { id },
      })
    },
    openDirectory: () => {
      ensureReady()
      postToParent({
        protocol: BRIDGE_PROTOCOL.CHANNEL,
        version: BRIDGE_PROTOCOL.VERSION,
        type: BRIDGE_EVENTS.NAV_OPEN_DIRECTORY,
        id: createId(),
        ts: Date.now(),
        meta: options?.metaGetter?.() ?? {},
        payload: {},
      })
    },
  }

  return { attach, detach, sendSync, nav, disconnect }
}
